import React, { Component, ReactNode } from 'react';
import { 
  Button, 
  Card, 
  Alert,
  Collapse,
  Space,
  Typography,
  Divider
} from 'antd';
import { 
  ExclamationCircleOutlined,
  ReloadOutlined,
  HomeOutlined,
  DownloadOutlined,
  ReconciliationOutlined
} from '@ant-design/icons';
import ErrorHandler from '../../utils/errorHandling';
import DataRecovery from '../../utils/dataRecovery';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string | null;
  retryCount: number;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showErrorDetails?: boolean;
  maxRetries?: number;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { onError } = this.props;
    
    // Log error through our error handling system
    ErrorHandler.handleUnexpectedError(
      error,
      errorInfo.componentStack || 'Unknown component stack',
      'error-boundary'
    );

    // Create backup before crash
    try {
      const currentData = this.getCurrentAppData();
      if (currentData) {
        DataRecovery.createBackup(currentData, 'crash-recovery');
      }
    } catch (backupError) {
      console.error('Failed to create crash backup:', backupError);
    }

    this.setState({
      errorInfo,
      error
    });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Auto-retry for network errors (with exponential backoff)
    if (this.isNetworkError(error) && this.state.retryCount < (this.props.maxRetries || 3)) {
      const delay = Math.pow(2, this.state.retryCount) * 1000; // 1s, 2s, 4s
      this.retryTimeout = setTimeout(() => {
        this.handleRetry();
      }, delay);
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  private getCurrentAppData(): any {
    // Try to extract current application data from various sources
    try {
      // Check if data is stored in a global state manager or context
      const appData = (window as any).__APP_DATA__ || 
                     JSON.parse(localStorage.getItem('mj-estimator-current') || 'null') ||
                     JSON.parse(sessionStorage.getItem('mj-estimator-current') || 'null');
      
      return appData;
    } catch (error) {
      return null;
    }
  }

  private isNetworkError(error: Error): boolean {
    return error.message.includes('fetch') || 
           error.message.includes('network') ||
           error.message.includes('Failed to fetch') ||
           error.name === 'NetworkError';
  }

  private handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  private handleReload = () => {
    // Clear crash marker before reload
    DataRecovery.clearCrashMarker();
    window.location.reload();
  };

  private handleGoHome = () => {
    // Clear crash marker and navigate to home
    DataRecovery.clearCrashMarker();
    window.location.href = '/';
  };

  private handleReportError = () => {
    const { error, errorInfo, errorId } = this.state;
    
    if (!error) return;

    const errorReport = {
      errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      errorBoundary: 'ErrorBoundary',
      additionalInfo: ErrorHandler.exportErrorLog()
    };

    // Download error report
    const blob = new Blob([JSON.stringify(errorReport, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-report-${errorId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  private handleRecoverData = () => {
    const backups = DataRecovery.getAvailableBackups();
    if (backups.length > 0) {
      const latestBackup = backups[0];
      const recoveredData = DataRecovery.restoreFromBackup(latestBackup.id);
      
      if (recoveredData) {
        // Store recovered data and reload
        localStorage.setItem('mj-estimator-recovered', JSON.stringify(recoveredData));
        this.handleReload();
      }
    }
  };

  render() {
    const { hasError, error, errorId, retryCount } = this.state;
    const { children, fallback, showErrorDetails = false, maxRetries = 3 } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <Card className="max-w-2xl w-full shadow-lg">
            <div className="text-center p-8">
              {/* Error Icon */}
              <ExclamationCircleOutlined className="text-6xl text-red-500 mb-4 animate-pulse" />

              {/* Error Message */}
              <Title level={2} className="text-red-600 mb-2">
                Something went wrong
              </Title>

              <Text className="text-gray-600 text-lg mb-6 block">
                We apologize for the inconvenience. The application encountered an unexpected error.
              </Text>

              {/* Error ID */}
              <Alert 
                type="info" 
                message={
                  <Text code className="text-xs">
                    Error ID: {errorId}
                  </Text>
                } 
                className="mb-6 text-left"
              />

              {/* Recovery Options */}
              <Space direction="vertical" size="middle" className="w-full mb-6">
                {retryCount < maxRetries && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<ReloadOutlined />}
                    onClick={this.handleRetry}
                    className="w-full"
                  >
                    Try Again {retryCount > 0 && `(Attempt ${retryCount + 1}/${maxRetries})`}
                  </Button>
                )}

                <Space className="w-full">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={this.handleReload}
                    className="flex-1"
                  >
                    Reload Page
                  </Button>

                  <Button
                    icon={<HomeOutlined />}
                    onClick={this.handleGoHome}
                    className="flex-1"
                  >
                    Go Home
                  </Button>
                </Space>

                {/* Data Recovery */}
                {DataRecovery.getAvailableBackups().length > 0 && (
                  <Button
                    type="dashed"
                    icon={<ReconciliationOutlined />}
                    onClick={this.handleRecoverData}
                    className="w-full text-green-600 border-green-300 hover:border-green-500"
                  >
                    Recover Recent Work
                  </Button>
                )}
              </Space>

              <Divider />

              {/* Advanced Options */}
              <Collapse ghost className="text-left">
                <Panel header="Advanced Options" key="1">
                  <Space direction="vertical" className="w-full">
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={this.handleReportError}
                      block
                    >
                      Download Error Report
                    </Button>

                    {showErrorDetails && (
                      <div className="mt-4">
                        <Title level={5} className="mb-2">
                          Error Details:
                        </Title>
                        <div className="bg-gray-100 p-4 rounded max-h-48 overflow-auto font-mono text-xs">
                          <Text className="whitespace-pre-wrap">
                            {error.stack}
                          </Text>
                        </div>
                      </div>
                    )}
                  </Space>
                </Panel>
              </Collapse>

              {/* Help Text */}
              <Text type="secondary" className="text-xs mt-4 block">
                If this problem persists, please contact support with the error ID above.
              </Text>
            </div>
          </Card>
        </div>
      );
    }

    return children;
  }
}


export default ErrorBoundary;